/* eslint-disable import/extensions */
import React, { useEffect, useState, useRef } from "react";
import logger from "../../utils/logger.js";
import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CloseIcon from '@mui/icons-material/Close';
import { getSenderColor } from '../../utils/senderColor';
import { IMessage, FileBlock, DocumentCitationBlock } from "../../models/Message";
import { useCanvasPanel } from '../../contexts/CanvasPanelContext';
import { useStreamingMessage } from '../../hooks/useStreamingMessage';
import {
  Avatar,
  Box,
  Stack,
  Paper,
  Typography,
  Chip,
  IconButton,
  alpha,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { toast } from 'react-toastify';
import { getFileURL, getRagSourceBlob, getRagSourceURL, toggleReaction } from '../../resources/api.resource.ts';
import ContentBlockRenderer from './ContentBlockRenderer';

interface MessageComponentProps {
  message: IMessage;
  uId: string;
  chatType: string;
  collaborationId: string;
  collaborationIdSummary?: string;
  tenantId: string;
  allUsersMap?: Record<string, { name: string; email: string; userType: 'user' | 'agent'; avatarUrl?: string | null }>;
  /** Shared WebSocket from Workspaces — only provided for streaming messages */
  socket?: WebSocket;
}

const MessageComponent: React.FC<MessageComponentProps> = ({
  message,
  uId,
  chatType,
  collaborationId,
  collaborationIdSummary,
  tenantId,
  allUsersMap = {},
  socket,
}) => {
  const theme = useTheme();
  const chat = {
    // The bg fallback used to be #EFF1F4, which paired with the white text
    // fallback for 1.13:1 — invisible. Only the theme override kept it legible.
    userBubbleBg:          theme.palette.chat?.userBubbleBg          ?? '#225CAA',
    userBubbleText:        theme.palette.chat?.userBubbleText        ?? '#FFFFFF',
    assistantBubbleBg:     theme.palette.chat?.assistantBubbleBg     ?? '#FFFFFF',
    assistantBubbleBorder: theme.palette.chat?.assistantBubbleBorder ?? '#E1E4E8',
    bubbleText:            theme.palette.chat?.bubbleText            ?? '#1A1A1A',
    sentStatus:            theme.palette.chat?.sentStatus            ?? '#5F6368',
    reactionAction:        theme.palette.chat?.reactionAction        ?? '#5F6368',
    reactionActionHover:   theme.palette.chat?.reactionActionHover   ?? '#1A1A1A',
    systemBubbleBg:        theme.palette.chat?.systemBubbleBg        ?? '#F4F5F7',
  };

  const { contentBlocks: streamingBlocks, isStreaming, error: streamError } = useStreamingMessage({
    message,
    socket: socket ?? null,
    collaborationId,
    enabled: message.status === 'streaming',
  });

  const displayBlocks = message.status === 'streaming' ? streamingBlocks : message.contentBlocks;

  const [align, setAlign] = useState("left");
  const [systemMessage] = useState(message?.sender?.role === "system");
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const feedbackTitleId = React.useId();
  const [selectedEmoji, setSelectedEmoji] = useState<string>("");
  const [feedbackText, setFeedbackText] = useState("");
  const [selectedIssue, setSelectedIssue] = useState<string>("");
  const [submittedReaction, setSubmittedReaction] = useState<'👍' | '👎' | null>(() => {
    const reactions = message.interactions?.reactions ?? {};
    const uid = uId.toLowerCase();
    if (reactions['👍']?.some((id: string) => id.toLowerCase() === uid)) return '👍';
    if (reactions['👎']?.some((id: string) => id.toLowerCase() === uid)) return '👎';
    return null;
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [aiMetadataAnchor, setAiMetadataAnchor] = useState<{ top: number; left: number } | null>(null);
  const [loadingFileName, setLoadingFileName] = useState<string | null>(null);

  const messageRef = useRef<HTMLDivElement>(null);
  const citationPreviewObjectUrlRef = useRef<string | null>(null);
  const canvas = useCanvasPanel();

  useEffect(() => {
    return () => {
      if (citationPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(citationPreviewObjectUrlRef.current);
        citationPreviewObjectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!message.sender.id) return;
    setAlign(message.sender.id.toUpperCase() === uId.toUpperCase() ? "right" : "left");
  }, [message, uId]);

  useEffect(() => {
    const reactions = message.interactions?.reactions ?? {};
    const uid = uId.toLowerCase();
    if (reactions['👍']?.some((id: string) => id.toLowerCase() === uid)) setSubmittedReaction('👍');
    else if (reactions['👎']?.some((id: string) => id.toLowerCase() === uid)) setSubmittedReaction('👎');
  }, [message.interactions?.reactions, uId]);

  const isUserMessage = align === "right";
  const isAssistantMessage = message.sender.role === 'assistant';
  const isSystemMessage = systemMessage;
  const senderColor = !isUserMessage && !isAssistantMessage && !isSystemMessage
    ? getSenderColor(message.sender.id)
    : null;

  // For the assistant, resolve the avatar the same way the header does — from
  // allUsersMap keyed by sender id — rather than the (often stale) avatarUrl on
  // the message payload. Human messages keep using the sender's own avatarUrl.
  const senderAvatarUrl = isAssistantMessage
    ? (message.sender.id ? allUsersMap[message.sender.id.toLowerCase()]?.avatarUrl || undefined : undefined)
    : (message.sender.avatarUrl || undefined);

  const handleReaction = async (emoji: string) => {
    // Only dislike opens the feedback dialog; like submits the reaction directly.
    if (isAssistantMessage && emoji === '👎') {
      setSelectedEmoji(emoji);
      setShowFeedbackDialog(true);
      return;
    }

    const isAssistantLike = isAssistantMessage && emoji === '👍';
    try {
      await toggleReaction({
        collaboration_id: collaborationId,
        message_id: message.id,
        emoji,
        action: "toggle",
        feedback: "",
        rating: isAssistantLike ? 1 : null,
      });
      if (isAssistantLike) {
        setSubmittedReaction('👍');
      }
      logger.event("Reaction Added", { messageId: message.id, emoji, userId: uId });
    } catch (err: any) {
      console.error('Failed to toggle reaction:', err);
      toast.error('Failed to update reaction. Please try again.');
    }
  };

  const handleFeedbackSubmit = async () => {
    const mappedRating = selectedEmoji === '👍' ? 1 : -1;
    const combinedFeedback = [selectedIssue, feedbackText].filter(Boolean).join(' — ');
    setIsSubmittingFeedback(true);
    try {
      await toggleReaction({
        collaboration_id: collaborationId,
        message_id: message.id,
        emoji: selectedEmoji,
        action: "toggle",
        feedback: combinedFeedback,
        rating: mappedRating,
      });
      logger.event("Reaction Added", {
        messageId: message.id,
        emoji: selectedEmoji,
        userId: uId,
        feedbackProvided: combinedFeedback.length > 0,
        rating: mappedRating,
      });
      setSubmittedReaction(selectedEmoji as '👍' | '👎');
      setShowFeedbackDialog(false);
      setSelectedEmoji("");
      setFeedbackText("");
      setSelectedIssue("");
    } catch (err: any) {
      console.error('Failed to toggle reaction:', err);
      toast.error('Failed to update reaction. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackDialog(false);
    setSelectedEmoji("");
    setFeedbackText("");
    setSelectedIssue("");
  };

  // Toggle the AI metadata popover on click: open it anchored to the button, or
  // close it if it's already open. A second click on the button (or a click away)
  // dismisses it.
  const handleAiMetadataClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (aiMetadataAnchor) {
      setAiMetadataAnchor(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setAiMetadataAnchor({
      top: rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX,
    });
  };

  const handleAiMetadataClose = () => {
    setAiMetadataAnchor(null);
  };

  const getFileObj = (fileBlock: FileBlock) => {
    const parts = fileBlock.url?.split("/") ?? [];
    const collaborationsIdx = parts.indexOf("collaborations");
    const url_collaboration_id =
      collaborationsIdx >= 0 && parts.length > collaborationsIdx + 1
        ? parts[collaborationsIdx + 1]
        : undefined;
    const instancesIdx = parts.indexOf("instances");
    const url_instance_id =
      instancesIdx >= 0 && parts.length > instancesIdx + 1
        ? parts[instancesIdx + 1]
        : undefined;
    return {
      collaboration_id: url_collaboration_id,
      collaboration_instance_id: url_instance_id,
      file_name: fileBlock.url?.split("/").pop(),
    };
  };

  const openFile = async (fileBlock: FileBlock) => {
    setLoadingFileName(fileBlock.fileName);
    try {
      const resp = await getFileURL(getFileObj(fileBlock));
      const uri = resp?.url ?? fileBlock.url;
      const ext = fileBlock.fileName.toLowerCase().split('.').pop() || '';
      const mimeType = ext === 'pdf'
        ? 'application/pdf'
        : ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
          ? `image/${ext === 'jpg' ? 'jpeg' : ext}`
          : undefined;
      canvas.loadDocument({ uri, title: fileBlock.fileName, ...(mimeType && { mimeType }) });
    } finally {
      setLoadingFileName(null);
    }
  };

  const downloadFile = async (fileBlock: FileBlock) => {
    setLoadingFileName(fileBlock.fileName);
    try {
      const resp = await getFileURL(getFileObj(fileBlock));
      if (resp?.url) {
        window.open(resp.url, '_blank');
      }
    } finally {
      setLoadingFileName(null);
    }
  };

  const resolveImageUrl = async (url: string): Promise<string> => {
    const resp = await getFileURL(getFileObj({ url, fileName: url.split('/').pop() || '' } as FileBlock));
    return resp?.url ?? url;
  };

  const isChatAttachmentCitation = (citation: DocumentCitationBlock) => {
    const parts = citation.sourceId?.split("/") ?? [];
    return parts.includes("collaborations") && parts.includes("instances");
  };

  const getCitationTitle = (citation: DocumentCitationBlock) =>
    citation.documentFilename || citation.title || citation.uri;

  const getCitationPage = (citation: DocumentCitationBlock) => {
    if (citation.chunkPage === undefined || citation.chunkPage === null) return undefined;
    const page = typeof citation.chunkPage === 'number'
      ? citation.chunkPage
      : Number.parseInt(String(citation.chunkPage), 10);
    return Number.isFinite(page) && page > 0 ? page : undefined;
  };

  const getRagSourceRequest = (citation: DocumentCitationBlock) => ({
    document_storage_account: citation.documentStorageAccount,
    document_container: citation.documentContainer,
    document_path: citation.documentPath || citation.documentFilename || citation.uri,
    document_filename: citation.documentFilename || citation.uri,
  });

  const resolveCitationPreviewUrl = async (citation: DocumentCitationBlock): Promise<string> => {
    if (citation.documentContainer) {
      const blob = await getRagSourceBlob(getRagSourceRequest(citation));
      if (citationPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(citationPreviewObjectUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(blob);
      citationPreviewObjectUrlRef.current = objectUrl;
      return objectUrl;
    }
    return resolveCitationUrl(citation);
  };

  const resolveCitationUrl = async (citation: DocumentCitationBlock): Promise<string> => {
    if (citation.documentContainer) {
      const resp = await getRagSourceURL(getRagSourceRequest(citation));
      if (resp?.url) return resp.url;
    }

    if (isChatAttachmentCitation(citation)) {
      const parts = citation.sourceId?.split("/") ?? [];
      const collaborationsIdx = parts.indexOf("collaborations");
      const instancesIdx = parts.indexOf("instances");
      const resp = await getFileURL({
        collaboration_id: collaborationsIdx >= 0 ? parts[collaborationsIdx + 1] : undefined,
        collaboration_instance_id: instancesIdx >= 0 ? parts[instancesIdx + 1] : undefined,
        file_name: citation.sourceId?.split("/").pop(),
      });
      if (resp?.url) return resp.url;
    }

    if (citation.uri?.startsWith("http://") || citation.uri?.startsWith("https://")) {
      return citation.uri;
    }
    return citation.sourceId;
  };

  const viewCitation = async (citation: DocumentCitationBlock) => {
    setLoadingFileName(citation.uri);
    try {
      const uri = await resolveCitationPreviewUrl(citation);
      const page = getCitationPage(citation);
      canvas.loadDocument({
        uri,
        title: getCitationTitle(citation),
        mimeType: 'application/pdf',
        sourceId: citation.sourceId,
        ...(page && { page }),
      });
    } finally {
      setLoadingFileName(null);
    }
  };

  const downloadCitation = async (citation: DocumentCitationBlock) => {
    setLoadingFileName(citation.uri);
    try {
      const uri = await resolveCitationUrl(citation);
      window.open(uri, '_blank');
    } finally {
      setLoadingFileName(null);
    }
  };

  return (
    <Box
      ref={messageRef}
      sx={{
        display: 'flex',
        justifyContent: isSystemMessage ? 'center' : (isUserMessage ? 'flex-end' : 'flex-start'),
        mb: 3,
        px: 2,
        ...(message.isPending && {
          opacity: 0.6,
          filter: 'blur(0.5px)',
          transition: 'opacity 0.3s ease, filter 0.3s ease',
        }),
      }}
    >
      {/* System Message */}
      {isSystemMessage && (
        <Paper
          elevation={0}
          sx={{
            maxWidth: { xs: '92%', sm: '60%' },
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: chat.systemBubbleBg,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <InfoIcon color="info" fontSize="small" />
          <Typography variant="body2" data-testid="message-text" sx={{ textAlign: 'center' }}>
            <ContentBlockRenderer
              blocks={displayBlocks}
              onFileOpen={openFile}
              onFileDownload={downloadFile}
              onCitationView={viewCitation}
              onCitationDownload={downloadCitation}
              onResolveImageUrl={resolveImageUrl}
              userMap={allUsersMap}
              loadingFileName={loadingFileName}
              isStreaming={isStreaming}
            />
          </Typography>
        </Paper>
      )}

      {/* Regular Messages (Human/AI) */}
      {!isSystemMessage && (
        <Stack
          direction={isUserMessage ? 'row-reverse' : 'row'}
          spacing={1.5}
          alignItems="flex-start"
          maxWidth={{ xs: '92%', sm: '75%' }}
        >
          {/* Avatar — photo if available, icon/initial fallback.
              aria-hidden: it is a duplicate of the sender name above, and the
              initial-letter fallback would otherwise be read out as a stray
              character. */}
          <Avatar
            aria-hidden="true"
            alt=""
            src={senderAvatarUrl}
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              ...(isAssistantMessage && (senderAvatarUrl
                ? {
                    bgcolor: '#fff',
                    border: `1px solid ${chat.assistantBubbleBorder}`,
                    // Show the full logo (not cropped) with a little breathing room.
                    '& .MuiAvatar-img': { objectFit: 'contain', p: '3px', boxSizing: 'border-box' },
                  }
                : { bgcolor: theme.palette.primary.main, color: '#fff' })),
              ...(senderColor && { bgcolor: senderColor.avatarBg, color: senderColor.avatarFg }),
            }}
          >
            {isAssistantMessage
              ? <SmartToyIcon fontSize="small" />
              : message.sender.displayName?.charAt(0).toUpperCase()}
          </Avatar>

          {/* Bubble column */}
          <Stack
            spacing={0.5}
            flex={1}
            alignItems={isUserMessage ? 'flex-end' : 'flex-start'}
          >
            {/* Message bubble */}
            <Paper
              elevation={0}
              data-testid="message-text"
              sx={{
                px: 2,
                py: 1.5,
                color: chat.bubbleText,
                wordWrap: 'break-word',
                overflow: 'hidden',
                ...(isUserMessage ? {
                  bgcolor: chat.userBubbleBg,
                  color: chat.userBubbleText,
                  borderRadius: '16px 4px 16px 16px',
                } : {
                  bgcolor: chat.assistantBubbleBg,
                  border: `1px solid ${chat.assistantBubbleBorder}`,
                  borderRadius: '4px 16px 16px 16px',
                }),
              }}
            >
              {/* Who sent this. On screen the sender is conveyed only by which
                  side the bubble sits on and its colour — no use to a screen
                  reader working through the transcript linearly (1.3.1/1.4.1).
                  Lives inside the bubble rather than as a Stack child because
                  Stack's `spacing` margins every child after the first, so a
                  leading sibling would visibly shift the avatar. */}
              <span className="sr-only">
                {isUserMessage ? 'You' : message.sender.displayName}
                {': '}
              </span>
              <ContentBlockRenderer
                blocks={displayBlocks}
                onFileOpen={openFile}
                onFileDownload={downloadFile}
                onCitationView={viewCitation}
                onCitationDownload={downloadCitation}
                onResolveImageUrl={resolveImageUrl}
                userMap={allUsersMap}
                loadingFileName={loadingFileName}
                isStreaming={isStreaming}
                onColoredSurface={isUserMessage}
              />
              {streamError && (
                // Sits inside the transcript's role="log", so it is announced as
                // an addition once the log stops being busy. aria-atomic keeps it
                // from being merged into whatever partial text streamed before
                // the failure.
                <Typography
                  variant="body2"
                  color="error"
                  aria-atomic="true"
                  sx={{ mt: 1, fontSize: '0.8rem' }}
                >
                  Agent response failed to load.
                </Typography>
              )}
            </Paper>

            {/* Below user bubble: Sent status */}
            {isUserMessage && (
              <Typography
                variant="caption"
                sx={{ color: chat.sentStatus, fontSize: '0.7rem', pr: 0.5 }}
              >
                ✓✓ Sent
              </Typography>
            )}

            {/* Below assistant bubble: Like / Dislike + AI metadata */}
            {!isUserMessage && (
              <Stack direction="row" spacing={0} alignItems="center">
                <Button
                  size="small"
                  variant="text"
                  startIcon={
                    submittedReaction === '👍'
                      ? <ThumbUpIcon sx={{ fontSize: '0.85rem !important', color: '#0857C3' }} />
                      : <ThumbUpOutlinedIcon sx={{ fontSize: '0.85rem !important' }} />
                  }
                  onClick={() => submittedReaction === null && handleReaction('👍')}
                  sx={{
                    color: submittedReaction === '👍' ? '#0857C3' : chat.reactionAction,
                    fontWeight: submittedReaction === '👍' ? 600 : 400,
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    py: 0.25,
                    textTransform: 'none',
                    cursor: submittedReaction === null ? 'pointer' : 'default',
                    '&:hover': {
                      color: submittedReaction === null ? chat.reactionActionHover : (submittedReaction === '👍' ? '#0857C3' : chat.reactionAction),
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Like
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={
                    submittedReaction === '👎'
                      ? <ThumbDownIcon sx={{ fontSize: '0.85rem !important', color: '#0857C3' }} />
                      : <ThumbDownOutlinedIcon sx={{ fontSize: '0.85rem !important' }} />
                  }
                  onClick={() => submittedReaction === null && handleReaction('👎')}
                  sx={{
                    color: submittedReaction === '👎' ? '#0857C3' : chat.reactionAction,
                    fontWeight: submittedReaction === '👎' ? 600 : 400,
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    py: 0.25,
                    textTransform: 'none',
                    cursor: submittedReaction === null ? 'pointer' : 'default',
                    '&:hover': {
                      color: submittedReaction === null ? chat.reactionActionHover : (submittedReaction === '👎' ? '#0857C3' : chat.reactionAction),
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Dislike
                </Button>
                {isAssistantMessage && message.aiMetadata && (
                  <IconButton
                    size="small"
                    onClick={handleAiMetadataClick}
                    aria-label="View AI response details"
                    sx={{
                      color: chat.reactionAction,
                      p: 0.5,
                      '&:hover': { color: chat.reactionActionHover, bgcolor: 'transparent' },
                    }}
                  >
                    <InfoIcon sx={{ fontSize: '0.95rem' }} />
                  </IconButton>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}

      {/* Feedback Dialog for Assistant Reactions */}
      <Dialog
        open={showFeedbackDialog}
        onClose={handleFeedbackCancel}
        maxWidth="xs"
        fullWidth
        aria-labelledby={feedbackTitleId}
        slotProps={{ paper: { sx: { borderRadius: '12px', p: 0 } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, pt: 2.5, px: 3 }}>
          <Typography id={feedbackTitleId} sx={{ fontFamily: "var(--app-font-family, 'Proxima Nova')", fontWeight: 600, fontSize: '1.125rem', color: '#1A1F2E' }}>
            Provide Feedback
          </Typography>
          <IconButton size="small" aria-label="Close feedback dialog" onClick={handleFeedbackCancel} sx={{ color: '#8E97A3' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, pt: 0.5 }}>
          <Typography sx={{ fontFamily: "var(--app-font-family, 'Proxima Nova')", fontWeight: 500, fontSize: '0.875rem', color: '#1A1F2E', mb: 1.5 }}>
            What did you think about this response?
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              displayEmpty
              value={selectedIssue}
              onChange={(e) => setSelectedIssue(e.target.value)}
              sx={{ borderRadius: '8px', fontFamily: "var(--app-font-family, 'Proxima Nova')", fontSize: '0.875rem' }}
              renderValue={(v) => v || <span style={{ color: '#8E97A3' }}>Select an issue</span>}
            >
              <MenuItem value="Incorrect or incomplete response">Incorrect or incomplete response</MenuItem>
              <MenuItem value="Not what I asked for">Not what I asked for</MenuItem>
              <MenuItem value="Safety or legal concern">Safety or legal concern</MenuItem>
            </Select>
          </FormControl>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Help us improve by sharing your thoughts (optional)"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem', fontFamily: "var(--app-font-family, 'Proxima Nova')" },
              '& textarea::placeholder': { color: '#8E97A3', opacity: 1 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleFeedbackCancel}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none', fontFamily: "var(--app-font-family, 'Proxima Nova')", fontWeight: 600, fontSize: '0.875rem', color: '#1A1F2E', borderColor: '#D0D5DD', px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFeedbackSubmit}
            variant="contained"
            disableElevation
            disabled={isSubmittingFeedback}
            startIcon={isSubmittingFeedback ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
            sx={{ borderRadius: '8px', textTransform: 'none', fontFamily: "var(--app-font-family, 'Proxima Nova')", fontWeight: 600, fontSize: '0.875rem', bgcolor: '#0857C3', '&:hover': { bgcolor: '#0648a8' }, '&.Mui-disabled': { bgcolor: '#0857C3', opacity: 0.7 }, px: 2.5 }}
          >
            {isSubmittingFeedback ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Metadata Popover */}
      <Popover
        open={Boolean(aiMetadataAnchor) && Boolean(message.aiMetadata)}
        anchorReference="anchorPosition"
        anchorPosition={aiMetadataAnchor || { top: 0, left: 0 }}
        onClose={handleAiMetadataClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              maxWidth: 320,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <InfoIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              AI Response Details
            </Typography>
          </Box>
          {message.aiMetadata && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                  MODEL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {message.aiMetadata.modelId}
                  </Typography>
                </Box>
              </Box>
              {message.aiMetadata.tokenUsage && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    TOKEN USAGE
                  </Typography>
                  <Stack spacing={0.75}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Prompt</Typography>
                      <Chip
                        label={message.aiMetadata.tokenUsage.prompt.toLocaleString()}
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, backgroundColor: (t) => alpha(t.palette.info.main, 0.1), color: 'info.main' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Completion</Typography>
                      <Chip
                        label={message.aiMetadata.tokenUsage.completion.toLocaleString()}
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, backgroundColor: (t) => alpha(t.palette.success.main, 0.1), color: 'success.main' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.85rem', fontWeight: 600 }}>Total</Typography>
                      <Chip
                        label={message.aiMetadata.tokenUsage.total.toLocaleString()}
                        size="small"
                        sx={{ height: 24, fontSize: '0.8rem', fontWeight: 700, backgroundColor: (t) => alpha(t.palette.primary.main, 0.15), color: 'primary.main' }}
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default MessageComponent;
